import { join } from 'path';
import type { v2, ViewTemplateType } from '@sap/ux-specification-types';
import { DirName } from '@sap/ux-specification-types';

import { render } from 'ejs';
import { readFileSync } from 'fs';
import { type IdGeneratorFunction, getRelativeTemplateComponentPath } from '@sap-ux/fe-fpm-writer';

const templatesPath = join(__dirname, 'templates');

export enum ViewTypes {
    XML = 'xml',
    HTML = 'html',
    JSON = 'json'
}

export const CONFIG = {
    ['AnalyticalTableColumnsExtension.xml']: {
        getData: (generateId: IdGeneratorFunction): { ids: Record<string, string> } => {
            return {
                ids: {
                    label: generateId('Label'),
                    text: generateId('Text'),
                    customData: generateId('CustomData')
                }
            };
        }
    },
    ['fragment.xml']: {
        getData: (generateId: IdGeneratorFunction): { ids: Record<string, string> } => {
            return {
                ids: {
                    verticalLayout: generateId('VerticalLayout'),
                    text: generateId('Text'),
                    button: generateId('Button')
                }
            };
        }
    },
    ['GridTableColumnsExtension.xml']: {
        getData: (generateId: IdGeneratorFunction): { ids: Record<string, string> } => {
            return {
                ids: {
                    label: generateId('Label'),
                    text: generateId('Text'),
                    customData: generateId('CustomData')
                }
            };
        }
    },
    ['ResponsiveTableColumnsExtension.xml']: {
        getData: (generateId: IdGeneratorFunction): { ids: Record<string, string> } => {
            return {
                ids: {
                    text: generateId('Text'),
                    customData: generateId('CustomData')
                }
            };
        }
    },
    ['TreeTableColumnsExtension.xml']: {
        getData: (generateId: IdGeneratorFunction): { ids: Record<string, string> } => {
            return {
                ids: {
                    label: generateId('Label'),
                    text: generateId('Text'),
                    customData: generateId('CustomData')
                }
            };
        }
    },
    ['view.xml']: {
        getData: (generateId: IdGeneratorFunction): { ids: Record<string, string> } => {
            return {
                ids: {
                    app: generateId('App'),
                    page: generateId('Page'),
                    label: generateId('Label')
                }
            };
        }
    }
};

/**
 * Method which reads passed template and renders it using passed template object.
 *
 * @param {string} templatePath - Path to template.
 * @param {{ [key: string]: string }} templateData - Template data object.
 * @param {IdGeneratorFunction} generateId - function to generate ids for new entries in the fragment.
 * @returns {string} the rendered template's string
 */
export const renderTemplate = (
    templatePath: string,
    templateData: { [key: string]: string | boolean },
    generateId?: IdGeneratorFunction
): string => {
    const template = readFileSync(templatePath, 'utf8');
    const templateKey = getRelativeTemplateComponentPath(templatePath);
    if (generateId && CONFIG[templateKey] && typeof CONFIG[templateKey].getData === 'function') {
        const additionalData = CONFIG[templateKey].getData(generateId);
        templateData = { ...templateData, ...additionalData };
    }
    return render(template, templateData);
};

/**
 * Method escapes view name.
 *
 * @param {string} name - View's name.
 * @returns {string} Escaped view name.
 */
const escapeViewName = (name: string): string => {
    // Remove all special characters
    return name.replace(/[^\w\s-]/gi, '');
};

/**
 * Method which reads passed template and renders it using passed template object.
 *
 * @param {string} name - View's name.
 * @param {string} namespace - Application namespace.
 * @param fcl - Indicates whether the Flexible Column Layout (FCL) template should be used.
 * @param newControllerRouting - Specifies if the new controller routing should be applied.
 * @returns {string} the rendered template's string
 */
export const getControllerContent = (
    name: string,
    namespace: string,
    fcl = false,
    newControllerRouting = false
): string => {
    const templateData = {
        name: namespace + '.' + escapeViewName(name),
        newControllerRouting
    };
    return renderTemplate(join(templatesPath, fcl ? 'controllerFcl.js' : 'controller.js'), templateData);
};

/**
 * Method which reads passed template and renders it using passed template object.
 *
 * @param {string} name - View's name.
 * @param {string} namespace - Application namespace.
 * @param {ViewTypes} sourceType - View's source type like XML, HTML, JSON or JS.
 * @param {IdGeneratorFunction} generateId - function to generate ids for new entries in the fragment.
 * @param {boolean} [controller] - Generate controller.
 * @param {boolean} [page] - Is view should contains page structure or not - page structure uses 'sap.m.App' in view template.
 * @returns {string} the rendered template's string
 */
export const getViewContent = (
    name: string,
    namespace: string,
    sourceType: ViewTypes,
    generateId: IdGeneratorFunction,
    controller = false,
    page = true
): string => {
    const escapedName = escapeViewName(name);
    const templateData: { [key: string]: string | boolean } = {
        name: escapedName,
        controllerName: '',
        page
    };
    if (controller) {
        templateData.controllerName = namespace + '.controller.' + escapedName;
    }
    return renderTemplate(join(templatesPath, 'view.' + sourceType), templateData, generateId);
};

/**
 * Method which reads template for fragment and returns rendered result of fragment.
 *
 * @param {string} name - View's name.
 * @param {string} namespace - Application namespace.
 * @param {ViewTypes} sourceType - View's source type like XML, HTML, JSON or JS.
 * @param {IdGeneratorFunction} generateId - function to generate ids for new entries in the fragment.
 * @param {boolean} handler - JS handler.
 * @returns {string} the rendered template's string
 */
export const getFragmentContent = (
    name: string,
    namespace: string,
    sourceType: ViewTypes,
    generateId: IdGeneratorFunction,
    handler = false
): string => {
    const escapedName = escapeViewName(name);
    const templateData: { [key: string]: string } = {
        name: escapedName,
        handler: ''
    };
    if (handler) {
        templateData.handler = `${namespace.replace(/\./g, '/')}/${DirName.Fragment}/${escapedName}`;
    }
    return renderTemplate(join(templatesPath, 'fragment.' + sourceType), templateData, generateId);
};

/**
 * Method which reads passed template and renders it using passed template object.
 *
 * @returns {string} the rendered template's string
 */
export const getFragmentJsContent = (): string => {
    return renderTemplate(join(templatesPath, 'fragment.js'), {});
};

/**
 * Method which reads the template for a single column extension that shall be added to a fragment.
 *
 * @param column - data (from config) to be inserted in the template
 * @param template - The type of view template (e.g., XML, HTML, JSON).
 * @param {IdGeneratorFunction} generateId - function to generate ids for new entries in the fragment.
 * @returns {string} the rendered template's string
 */
export const getSingleColumnContent = (
    column: v2.TableCustomColumn,
    template: ViewTemplateType,
    generateId: IdGeneratorFunction
): string => {
    const templateData: { [key: string]: string } = {
        id: column.id,
        text: column.text,
        columnKey: column.columnKey,
        columnIndex: column.columnIndex !== undefined ? column.columnIndex.toString() : undefined,
        leadingProperty: column.leadingProperty
    };
    return renderTemplate(join(templatesPath, template + '.xml'), templateData, generateId);
};

/**
 * Method which reads the template for a column extension fragment and returns rendered result of fragment.
 *
 * @param customColumns - array of all column extensions' data
 * @param templateType - The type of view template (e.g., XML, HTML, JSON).
 * @param {IdGeneratorFunction} generateId - function to generate ids for new entries in the fragment.
 * @returns {string} the rendered template's string
 */
export const getColumnFragmentContent = (
    customColumns: v2.TableCustomColumns,
    templateType: ViewTemplateType,
    generateId: IdGeneratorFunction
): string => {
    let fragment = '';
    fragment = '<core:FragmentDefinition xmlns:core="sap.ui.core" xmlns:table="sap.ui.table" xmlns="sap.m">';
    if (customColumns) {
        for (const column of customColumns) {
            const newContent = getSingleColumnContent(column, templateType, generateId);
            fragment = fragment + '\n' + newContent;
        }
    }
    return fragment + '\n' + '</core:FragmentDefinition>';
};

/**
 * Creates the content for a ResponsiveTableCellsExtension fragment.
 *
 * @param customColumns - list of custom columns; for each of them a dummy entry gets created in the view content.
 * @param generateId - function to generate ids for new entries in the fragment.
 * @returns {string} the rendered template's string
 */
export const getResponsiveTableCellsFragment = (
    customColumns: v2.TableCustomColumns,
    generateId: IdGeneratorFunction
): string => {
    let fragment = '<core:FragmentDefinition xmlns:core="sap.ui.core" xmlns="sap.m">';
    let validatedIds: string[] = [];
    if (customColumns) {
        customColumns.forEach(() => {
            const newId = generateId('Text', validatedIds);
            validatedIds.push(newId);
            // use same approach as json2xml - use 4 spaces for fragment children elements
            fragment = fragment + `\n    <Text id="${newId}" text="Placeholder"/>`;
        });
    }
    return fragment + '\n' + '</core:FragmentDefinition>';
};

/**
 * Method to construct full name of view or fragment.
 *
 * @param {string} name Shorten name of view or fragment.
 * @param {boolean} isFragment Is fragment or view.
 * @param {ViewTypes} viewType type of view or fragment.
 * @returns {string} Full path of view or fragment name.
 */
export function getViewFileName(name: string, isFragment: boolean, viewType = ViewTypes.XML): string {
    return isFragment ? `${name}.fragment.${viewType}` : `${name}.view.${viewType}`;
}
